import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImageUploaderProps {
  images: File[];
  setImages: (images: File[]) => void;
  existingImages?: string[];
  setExistingImages?: (urls: string[]) => void;
  maxImages?: number;
  label?: string;
  hint?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  setImages,
  existingImages = [],
  setExistingImages,
  maxImages = 5,
  label,
  hint,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const total = images.length + existingImages.length;
  const remaining = Math.max(0, maxImages - total);
  const isFull = remaining === 0;

  // Preview-URL's één keer aanmaken en netjes weer vrijgeven; anders lekt elke
  // render een blob-URL.
  const previews = useMemo(() => images.map(file => URL.createObjectURL(file)), [images]);
  useEffect(() => () => previews.forEach(url => URL.revokeObjectURL(url)), [previews]);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const valid = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (valid.length === 0) return;
    // Nooit meer accepteren dan er nog ruimte is — de teller en de grens blijven zo gelijk.
    setImages([...images, ...valid].slice(0, Math.max(0, maxImages - existingImages.length)));
    if (inputRef.current) inputRef.current.value = '';
  };

  const openPicker = () => {
    if (!isFull) inputRef.current?.click();
  };

  const removeButton = (onRemove: () => void) => (
    <button
      type="button"
      className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 shadow-sm transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-homeme/40"
      onClick={e => { e.stopPropagation(); onRemove(); }}
      aria-label={t('remove_image', 'Afbeelding verwijderen')}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
        <path d="M5 5l10 10M15 5L5 15" />
      </svg>
    </button>
  );

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        {label && <span className="text-sm font-medium text-neutral-700">{label}</span>}
        <span className={`text-xs font-semibold tabular-nums ${isFull ? 'text-homeme' : 'text-neutral-500'}`}>
          {total} / {maxImages}
        </span>
      </div>

      <div
        role="button"
        tabIndex={isFull ? -1 : 0}
        aria-disabled={isFull}
        className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-7 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-homeme/30
          ${isFull
            ? 'cursor-not-allowed border-neutral-200 bg-neutral-50'
            : dragging
              ? 'cursor-pointer border-homeme bg-homeme/5'
              : 'cursor-pointer border-neutral-300 bg-neutral-50 hover:border-homeme hover:bg-homeme/5'}`}
        onClick={openPicker}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
        onDrop={e => { e.preventDefault(); setDragging(false); if (!isFull) handleFiles(e.dataTransfer.files); }}
        onDragOver={e => e.preventDefault()}
        onDragEnter={e => { e.preventDefault(); if (!isFull) setDragging(true); }}
        onDragLeave={e => { e.preventDefault(); setDragging(false); }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
          disabled={isFull}
        />
        <svg className={`mb-2 h-7 w-7 ${isFull ? 'text-neutral-300' : 'text-neutral-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2.5" />
          <circle cx="8.5" cy="9.5" r="1.6" />
          <path d="m3.5 17 4.8-4.6a2 2 0 0 1 2.7 0L16 17" />
          <path d="m14 14.5 1.7-1.6a2 2 0 0 1 2.7 0l2.1 2" />
        </svg>

        {isFull ? (
          <span className="text-sm text-neutral-500">
            {t('uploader_full', 'Maximum bereikt — verwijder een foto om een andere toe te voegen.')}
          </span>
        ) : (
          <>
            <span className="text-sm text-neutral-600">
              {t('uploader_drop', 'Sleep foto\'s hierheen of')}{' '}
              <span className="font-semibold text-homeme">{t('uploader_browse', 'kies bestanden')}</span>
            </span>
            <span className="mt-1 text-xs text-neutral-400">
              {t('uploader_remaining', 'Nog {{count}} mogelijk · JPG of PNG', { count: remaining })}
            </span>
          </>
        )}
      </div>

      {hint && <p className="mt-2 text-xs text-neutral-500">{hint}</p>}

      {(existingImages.length > 0 || images.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-3">
          {existingImages.map((url, idx) => (
            <div key={url} className="relative h-24 w-24">
              <img src={url} alt="" className="h-full w-full rounded-lg border border-neutral-200 object-cover" />
              {setExistingImages && removeButton(() => setExistingImages(existingImages.filter((_, i) => i !== idx)))}
            </div>
          ))}
          {images.map((file, idx) => (
            <div key={`${file.name}-${idx}`} className="relative h-24 w-24">
              <img src={previews[idx]} alt="" className="h-full w-full rounded-lg border border-neutral-200 object-cover" />
              {removeButton(() => setImages(images.filter((_, i) => i !== idx)))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
